import { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { fetchRatesGraph, fetchVaCurrencyList } from 'actions';
import {
  getDisplayValueForCharts,
  externalTooltipHandler,
  DEFAULT_MOBILE_DATE_FORMAT,
  parseDate,
} from 'utils';

import DatePicker from 'components/commons/DatePicker';
import TimesButtonGroup from 'components/commons/TimesButtonGroup';

import { ChartLayout } from 'components/ChartLayout';
import { CurrencyFilters } from 'components/CurrencyFilters';
import DropToggle from 'components/DropToggle';

import { Title, Text, DateButtons, InputsWrapper, Dash } from './styles';

const currencyOptions = [{ id: 'USD' }, { id: 'EUR' }, { id: 'PLN' }];
const currencyMbOptions = [{ id: 'USD' }, { id: 'EUR' }];

const termsButtons = [
  { id: 'week', name: 'dynamic.rates.chart.week', path: '/' },
  { id: 'month', name: 'dynamic.rates.chart.month', path: '/' },
  { id: 'quarter', name: 'dynamic.rates.chart.quarter', path: '/' },
  { id: 'year', name: 'dynamic.rates.chart.year', path: '/' },
  { id: 'max', name: 'dynamic.rates.chart.all', path: '/' },
];

const exchangerTermButtons = [
  {
    id: 'day',
    name: 'dynamic.rates.chart.today',
    path: '/',
  },
  { id: 'week', name: 'dynamic.rates.chart.week', path: '/' },
  { id: 'month', name: 'dynamic.rates.chart.month', path: '/' },
  { id: 'quarter', name: 'dynamic.rates.chart.quarter', path: '/' },
];

const interbankTermButtons = [
  {
    id: 'day',
    name: 'dynamic.rates.chart.today',
    path: '/',
  },
  { id: 'week', name: 'dynamic.rates.chart.week', path: '/' },
  { id: 'month', name: 'dynamic.rates.chart.month', path: '/' },
  { id: 'quarter', name: 'dynamic.rates.chart.quarter', path: '/' },
  { id: 'year', name: 'dynamic.rates.chart.year', path: '/' },
  { id: 'max', name: 'dynamic.rates.chart.all', path: '/' },
];

const MB_DAY_TICKS_COMPACT_MAX = 400;
const MB_DAY_TICKS_TABLET_MAX = 640;
const MB_DAY_TICKS_HORIZONTAL_MIN = 1024;

const getMbDayXTicks = chartWidth => {
  if (chartWidth && chartWidth < MB_DAY_TICKS_COMPACT_MAX) {
    return { maxTicksLimit: 4, maxRotation: 45, minRotation: 45 };
  }

  if (chartWidth && chartWidth < MB_DAY_TICKS_TABLET_MAX) {
    return { maxTicksLimit: 5, maxRotation: 0, minRotation: 0 };
  }

  if (chartWidth && chartWidth < MB_DAY_TICKS_HORIZONTAL_MIN) {
    return { maxTicksLimit: 6, maxRotation: 0, minRotation: 0 };
  }

  return { maxTicksLimit: 7, maxRotation: 0, minRotation: 0 };
};
export const DynamicRatesChart = ({
  params,
  changePeriod,
  currentOption,
  setCurrentOption,
  ratesGraphTimeLine,
  datasets,
  date,
  setDate,
  type,
  chartTitle,
  chartValues,
  page,
  isOpenChart,
  hasDateRange,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(isOpenChart);
  const currencyList = useSelector(state => state.currency.currencyList);
  const currencyVaList = useSelector(state => state.currency.currencyVaList);
  const dispatch = useDispatch();
  const [sortedMbCurrentOptions, setSortedMbCurrentOptions] = useState(
    currencyMbOptions
  );
  const isMbPage = page === 'interbank';
  const isExchangersPage = page === 'exchanger';
  const limitDayTicks = isMbPage && params.period === 'day';
  const chartFrameRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    if (!limitDayTicks || !isOpen) {
      return undefined;
    }

    const node = chartFrameRef.current;

    if (!node) {
      return undefined;
    }

    const updateWidth = () => {
      setChartWidth(node.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);

      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, [limitDayTicks, isOpen]);

  let buttons = [];
  let currenciesItems = null;

  if (isMbPage) {
    buttons = interbankTermButtons;
  } else if (isExchangersPage) {
    buttons = exchangerTermButtons;
    currenciesItems = currencyVaList;
  } else {
    buttons = termsButtons;
    currenciesItems = currencyList;
  }

  const currentCurrency = useMemo(() => {
    if (!isMbPage && currentOption) {
      return currenciesItems.filter(
        el => el.name.toLowerCase() === currentOption.toLowerCase()
      );
    }

    return '';
  }, [currentOption]);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchRatesGraph(params));
      dispatch(fetchVaCurrencyList({ intl }));
    }
  }, [
    dispatch,
    params.period,
    params.sourceCurrency,
    params.date?.custom?.from,
    params.date?.custom?.to,
    params.currentDate,
    isOpen,
  ]);

  useEffect(() => {
    if (isMbPage && currentOption === 'EUR') {
      setSortedMbCurrentOptions([...currencyMbOptions].reverse());
    }
  }, []);

  return (
    <>
      {isOpen && (
        <>
          <Title>
            <Text>
              <FormattedMessage
                id={chartTitle}
                values={{
                  currency:
                    currentCurrency[0]?.nameG || currentCurrency[0]?.text,
                  ...chartValues,
                }}
              />
            </Text>

            <CurrencyFilters
              primaryOptions={
                isMbPage ? sortedMbCurrentOptions : currencyOptions
              }
              value={currentOption}
              onChange={setCurrentOption}
              optionsList={currenciesItems}
            />
          </Title>

          <div ref={isMbPage ? chartFrameRef : undefined}>
            <ChartLayout
              id={params.type}
              type={type}
              labels={ratesGraphTimeLine}
              datasets={datasets}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                events: ['click', 'mousemove'],
                plugins: {
                  crosshair: {
                    line: {
                      color: '#A8B0AC',
                      width: 1,
                    },
                    zoom: {
                      enabled: false,
                    },
                  },
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    usePointStyle: true,
                    enabled: false,
                    external: context =>
                      externalTooltipHandler(context, params.type),
                    ...(isMbPage
                      ? {
                          callbacks: {
                            label(context) {
                              const formatted = new Intl.NumberFormat(
                                context.chart.options.locale,
                                {
                                  minimumFractionDigits: 4,
                                  maximumFractionDigits: 4,
                                }
                              ).format(context.parsed.y);
                              const label = context.dataset.label || '';

                              return label
                                ? `${label}: ${formatted}`
                                : formatted;
                            },
                          },
                        }
                      : {}),
                  },
                },
                interaction: {
                  mode: 'index',
                  intersect: false,
                },
                elements: {
                  line: {
                    tension: 0.2,
                  },
                  point: {
                    radius: 0,
                    hoverBackgroundColor: '#fff',
                    hoverBorderWidth: 3,
                    hoverRadius: 6,
                  },
                },
                scales: {
                  x: {
                    grid: {
                      display: false,
                    },
                    ticks: limitDayTicks
                      ? {
                          autoSkip: true,
                          ...getMbDayXTicks(chartWidth),
                          labelOffset: 1,
                          font: {
                            size: 13,
                          },
                          color: 'black',
                        }
                      : {
                          labelOffset: 1,
                          align: 'end',
                          font: {
                            size: 13,
                          },
                          color: 'black',
                        },
                  },
                  y: {
                    ticks: {
                      beginAtZero: false,
                      callback: yValue => {
                        return yValue.toFixed(2);
                      },
                      color: 'black',
                    },
                    grid: {
                      borderDash: [5, 5],
                      drawBorder: false,
                    },
                    grace: 0.2,
                  },
                },
              }}
            />
          </div>
          <DateButtons $fitRow={isMbPage}>
            <TimesButtonGroup
              buttons={buttons}
              changePeriod={changePeriod}
              setDate={setDate}
              date={date}
              currentTerm={isMbPage || isExchangersPage ? 'day' : 'week'}
            />

            {hasDateRange && (
              <InputsWrapper $compact={isMbPage}>
                <DatePicker
                  getDisplayValue={getDisplayValueForCharts}
                  value={date.custom?.from || date.from}
                  onChange={e => {
                    if (setDate)
                      setDate({
                        ...date,
                        custom: {
                          ...date.custom,
                          from: parseDate(e)?.format(
                            DEFAULT_MOBILE_DATE_FORMAT
                          ),
                        },
                      });
                  }}
                />
                <Dash />
                <DatePicker
                  getDisplayValue={getDisplayValueForCharts}
                  value={date.custom?.to || date.to}
                  onChange={e => {
                    if (setDate)
                      setDate({
                        ...date,
                        custom: {
                          ...date.custom,
                          to: parseDate(e)?.format(DEFAULT_MOBILE_DATE_FORMAT),
                        },
                      });
                  }}
                />
              </InputsWrapper>
            )}
          </DateButtons>
        </>
      )}
      <DropToggle
        onPress={() => setIsOpen(!isOpen)}
        isOpen={isOpen}
        openTitle="dynamic.rates.chart.drop.open"
        closeTitle="dynamic.rates.chart.drop.close"
      />
    </>
  );
};

DynamicRatesChart.propTypes = {
  date: PropTypes.objectOf(PropTypes.string),
  setDate: PropTypes.func,
  changePeriod: PropTypes.func.isRequired,
  params: PropTypes.objectOf(PropTypes.string).isRequired,
  currentOption: PropTypes.string.isRequired,
  setCurrentOption: PropTypes.func.isRequired,
  ratesGraphTimeLine: PropTypes.arrayOf(PropTypes.string).isRequired,
  datasets: PropTypes.arrayOf(PropTypes.object).isRequired,
  type: PropTypes.string.isRequired,
  chartTitle: PropTypes.string.isRequired,
  chartValues: PropTypes.shape({}),
  page: PropTypes.string.isRequired,
  isOpenChart: PropTypes.bool,
  hasDateRange: PropTypes.bool,
};

DynamicRatesChart.defaultProps = {
  date: null,
  setDate: null,
  hasDateRange: true,
  chartValues: {},
  isOpenChart: true,
};
